import { useState } from 'react';
import type { KeyedMutator } from 'swr';
import { EngineService } from '@/services/engine';
import { CACHE_KEYS } from '@/constants/cache-keys';
import type { Person, UpdatePersonPayload } from '@/types/person';
import { revalidateByCacheKeyPrefix } from '@/utils/swr';

interface UsePersonUpdateOptions {
    mutateDetail?: KeyedMutator<Person>;
    onSuccess?: () => void;
    onError?: (error: unknown) => void;
    onSettled?: () => void;
}

export function usePersonUpdate({
    mutateDetail,
    onSuccess,
    onError,
    onSettled,
}: UsePersonUpdateOptions) {
    const [isUpdating, setIsUpdating] = useState(false);

    const updatePerson = async (
        personId: string,
        payload: UpdatePersonPayload,
    ) => {
        setIsUpdating(true);
        try {
            const updated = await EngineService.updatePerson(personId, payload);
            if (mutateDetail) {
                await mutateDetail(updated, { revalidate: false });
            }
            await revalidateByCacheKeyPrefix(CACHE_KEYS.ALL_PERSONS);
            onSuccess?.();
        } catch (error) {
            onError?.(error);
            throw error;
        } finally {
            setIsUpdating(false);
            onSettled?.();
        }
    };

    return { isUpdating, updatePerson };
}
